import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import ApplicationContext from 'components/Application/Context';
import * as session from 'store/authentication/session';
import {
  getCurrentImpersonationSession,
  isImpersonating,
  initializeImpersonationSession,
  endImpersonationSession,
  onImpersonationChange,
  cleanupImpersonationSession,
} from 'utils/impersonationSession';
import type {
  User,
  UseImpersonationReturn,
  RedirectDestination,
} from './types';

// Import the GraphQL operations
const IMPERSONATE_USER_MUTATION = gql`
  mutation ImpersonateUser($userId: Int!) {
    impersonateUser(userId: $userId) {
      accessToken
      user {
        id
        email
        firstName
        lastName
        role
        status
        flags
        team
        createdAt
        updatedAt
        lastAccess
      }
    }
  }
`;

const END_IMPERSONATION_MUTATION = gql`
  mutation EndImpersonation {
    endImpersonation {
      success
      message
    }
  }
`;

// Note: generateExchangeToken is called directly via fetch instead of Apollo
// to ensure we can control the Authorization header with the imp_ token

/**
 * Validate impersonation prerequisites
 */
function validateImpersonationStart(
  user: unknown,
  token: string | null,
  canImpersonate: boolean,
  userId: number,
): string | null {
  if (!user || !token) {
    return 'Must be authenticated to start impersonation';
  }

  if (!canImpersonate) {
    return 'Insufficient permissions to impersonate users';
  }

  if ((user as { id: number }).id === userId) {
    return 'You cannot impersonate yourself';
  }

  return null;
}

/**
 * Handle Showcase redirect with token exchange
 * Opens Showcase in a new tab with impersonation WITHOUT changing ben-app session
 * The admin stays logged in as admin in ben-app
 * The backend preserves the imp_ prefix through the exchange process,
 * so Showcase will correctly detect impersonation and show the banner.
 */
async function redirectToShowcase(accessToken: string) {
  try {
    // DON'T store impersonation session - we're not actually impersonating in ben-app
    // The admin stays logged in as admin in the main window

    // BYPASS APOLLO - Make direct fetch call with the impersonated token
    const apiResponse = await fetch('/api/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`, // Use the imp_ token directly!
      },
      body: JSON.stringify({
        query: `
          mutation GenerateExchangeToken($duration: Int!) {
            generateExchangeToken(duration: $duration) {
              token
              showcaseUrl
            }
          }
        `,
        variables: { duration: 300 },
      }),
    });

    const exchangeResult = await apiResponse.json();

    const {
      token: exchangeToken,
      showcaseUrl,
    } = exchangeResult.data.generateExchangeToken;

    // Add impersonation flag to ensure Showcase properly handles the session
    // This forces Showcase to clear any existing session before setting the new one
    const loginUrl = `${showcaseUrl}/token-exchange?exchangeToken=${exchangeToken}&isImpersonation=true`;

    // Open in new tab to keep admin session in ben-app
    const newTab = window.open(loginUrl, '_blank');
    if (!newTab) {
      // Fallback if popup blocked
      window.location.href = loginUrl;
    }
  } catch (error) {
    // Cannot navigate without knowing the showcase URL from backend
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(
      `Failed to generate exchange token for impersonation: ${errorMessage}`,
    );
  }
}

/**
 * Handle impersonation session setup
 * Note: Token exchange for Showcase is handled separately
 */
function setupImpersonationSession(
  originalToken: string,
  originalUser: unknown,
  impersonatedUser: unknown,
  accessToken: string,
  redirectDestination?: RedirectDestination,
) {
  // Store original admin session FIRST before any state updates
  initializeImpersonationSession(originalToken, originalUser as User);

  // Update session storage
  session.create({
    user: impersonatedUser,
    accessToken,
    permissions: session.getPermissions(),
    versioning: session.getVersioning(),
  });

  // For campaigns, redirect immediately
  // For showcase, token exchange is handled separately
  if (redirectDestination === 'campaigns') {
    setTimeout(() => {
      window.location.href = '/campaigns';
    }, 100);
  }
}

/**
 * Hook for managing impersonation in ben-app
 * Integrates with ApplicationContext and existing authentication patterns
 */
export function useImpersonation(): UseImpersonationReturn {
  const { user, token, setUser, setToken } = useContext(ApplicationContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clean up stale impersonation data on initialization if we detect self-impersonation or no auth
  React.useEffect(() => {
    const impersonationSession = getCurrentImpersonationSession();

    // If we have impersonation state but no token, clear it (broken state)
    if (impersonationSession && !token) {
      cleanupImpersonationSession();
      return;
    }

    // If we have impersonation state and a user, check for self-impersonation
    if (
      impersonationSession &&
      user &&
      impersonationSession.originalAdminUser.id === user.id
    ) {
      // Check if we're in a confused state (impersonating ourselves)
      cleanupImpersonationSession();
    }
  }, [user, token]);

  // GraphQL mutations
  const [impersonateUserMutation] = useMutation(IMPERSONATE_USER_MUTATION);
  const [endImpersonationMutation] = useMutation(END_IMPERSONATION_MUTATION);
  // Note: generateExchangeToken is called directly via fetch to bypass Apollo's cached auth

  // Get impersonation state from session storage
  const impersonationSession = getCurrentImpersonationSession();
  const currentlyImpersonating = isImpersonating();

  // Check if current user can impersonate
  // Admin and internal users can access the impersonation feature
  // Internal users may have restrictions on who they can impersonate (enforced by backend)
  const canImpersonate = Boolean(
    user &&
      (user.role === 'admin' || user.role === 'internal') &&
      !currentlyImpersonating,
  );

  const startImpersonation = useCallback(
    async (
      userId: number,
      redirectDestination: RedirectDestination = 'showcase',
    ) => {
      const validationError = validateImpersonationStart(
        user,
        token,
        canImpersonate,
        userId,
      );
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await impersonateUserMutation({
          variables: { userId },
        });

        const {
          accessToken,
          user: impersonatedUser,
        } = result.data.impersonateUser;

        // Handle redirect based on destination
        if (redirectDestination === 'showcase' && token) {
          // For Showcase, handle token exchange and redirect
          try {
            await redirectToShowcase(accessToken);
            // Exit immediately after redirect starts
            return;
          } catch (err) {
            // Errors are expected here if popup is blocked - just return silently
            return;
          }
        }

        // For campaigns destination, update state normally
        if (token) {
          setupImpersonationSession(
            token,
            user,
            impersonatedUser,
            accessToken,
            redirectDestination,
          );
        }

        // Update React state - this will trigger re-renders
        setToken(accessToken);
        setUser(impersonatedUser);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to start impersonation',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [user, token, canImpersonate, impersonateUserMutation, setToken, setUser],
  );

  const endImpersonation = useCallback(async () => {
    if (!currentlyImpersonating) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await endImpersonationMutation();
      const { success, message } = result.data.endImpersonation;

      if (!success) {
        throw new Error(message || 'Failed to end impersonation');
      }

      // Restore from session storage
      const restorationSuccess = endImpersonationSession();
      if (restorationSuccess && impersonationSession) {
        setToken(impersonationSession.originalAdminToken);
        setUser(impersonationSession.originalAdminUser);
      }
    } catch (err) {
      // If API call fails, try to restore from session storage
      const success = endImpersonationSession();
      if (success && impersonationSession) {
        setToken(impersonationSession.originalAdminToken);
        setUser(impersonationSession.originalAdminUser);
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to end impersonation',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    currentlyImpersonating,
    endImpersonationMutation,
    setToken,
    setUser,
    impersonationSession,
  ]);

  // Clear error when user changes
  useEffect(() => {
    if (error) {
      setError(null);
    }
  }, [user, error]);

  // Listen for impersonation changes across tabs
  useEffect(
    () =>
      onImpersonationChange((impersonating) => {
        // Force re-render when impersonation state changes in other tabs
        if (impersonating !== currentlyImpersonating) {
          // Trigger a re-render by updating a state variable
          window.location.reload(); // Simple approach: reload the page
        }
      }),
    [currentlyImpersonating],
  );

  return {
    isImpersonating: currentlyImpersonating,
    impersonatedUser: currentlyImpersonating ? user : null,
    originalAdminUser: impersonationSession?.originalAdminUser || null,
    canImpersonate,
    startImpersonation,
    endImpersonation,
    isLoading,
    error,
  };
}
